import { ApiService } from '@services/api.service';
import { TableDataApiParams } from '@services/practices.service';
import { Observable, throwError } from 'rxjs';
import { TablePaginationRespModel } from '@/models/table-pagination-resp.model';
import { catchError, map, tap } from 'rxjs/operators';
import { LoggerService } from '@services/logger.service';
import { RentalMovementModel } from '@/models/rentals/rental-movement-model';
import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { ReturnRentalMovementModel } from '@/models/rentals/return-rental-movement-model';

@Injectable({
  providedIn: 'root',
})
export class RentalMovementService extends ApiService {
  getDataForTable(
    params?: TableDataApiParams,
  ): Observable<TablePaginationRespModel<RentalMovementModel>> {
    const id = params.id;
    delete params.id;

    return new Observable<TablePaginationRespModel<RentalMovementModel>>((observer) => {
      this.http
        .get<TablePaginationRespModel<RentalMovementModel>>(
          this.baseUrl + `/rental-practices/${id}/rentals/page`,
          { params: { ...params } },
        )
        .pipe(
          tap((response) => {
            observer.next(response);
            observer.complete();
          }),
          catchError((error) => {
            console.error('Error occurred:', error.message);
            observer.error(error);

            return throwError(error);
          }),
        )
        .subscribe();
    });
  }

  downloadPdfVehiclePickUp(id: number) {
    return this.http.get(
      this.baseUrl + `/rentals/${id}/pdf-replacement-vehicle-pick-up?version=v3`,
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/octet-stream' }),
        responseType: 'blob',
      },
    );
  }

  downloadPdfVehicleDropOff(id: number) {
    return this.http.get(
      this.baseUrl + `/rentals/${id}/pdf-replacement-vehicle-drop-off?version=v3`,
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/octet-stream' }),
        responseType: 'blob',
      },
    );
  }

  downloadPdfRentalB2b(id: number) {
    return this.http.get(this.baseUrl + `/rentals/${id}/pdf-rental-b2b?version=v2`, {
      headers: new HttpHeaders({ 'Content-Type': 'application/octet-stream' }),
      responseType: 'blob',
    });
  }

  downloadPdfConsegnaCrespiRent(id: number) {
    return this.http.get(this.baseUrl + `/rentals/${id}/pdf-crespi-rent-consegna?version=v2`, {
      headers: new HttpHeaders({ 'Content-Type': 'application/octet-stream' }),
      responseType: 'blob',
    });
  }

  downloadPdfRiconsegnaCrespiRent(id: number) {
    return this.http.get(this.baseUrl + `/rentals/${id}/pdf-crespi-rent-rientro?version=v1`, {
      headers: new HttpHeaders({ 'Content-Type': 'application/octet-stream' }),
      responseType: 'blob',
    });
  }

  deleteRentalMovement(id: number) {
    return this.http.delete(this.baseUrl + `/rental-practices/${id}`).pipe(
      map(
        (response: any) =>
          tap(() => {
            LoggerService.log(response, 'deletePractice');

            this.triggerUpdate();
            this.toastr.success('Rental practice deleted successfully');
          }),
        catchError((error) => {
          this.toastr.error('Failed to delete rental practice');
          return throwError(error);
        }),
      ),
    );
  }

  async getRentalMovement(id: number) {
    return this.http
      .get<RentalMovementModel>(this.baseUrl + `/rental-practices/${id}/detail`)
      .toPromise();
  }

  getLatestRental(practiceId: number): Observable<RentalMovementModel | null> {
    return this.http
      .get<RentalMovementModel>(this.baseUrl + `/rental-practices/${practiceId}/rentals/latest`)
      .pipe(
        map((response: RentalMovementModel) => {
          LoggerService.log(response, 'getLatestRental');
          return response;
        }),
        catchError((error) => {
          // Se non esiste un noleggio precedente (404), restituiamo null
          if (error.status === 404) {
            LoggerService.log('No previous rental found', 'getLatestRental');
            return new Observable<null>((observer) => {
              observer.next(null);
              observer.complete();
            });
          }
          // Per altri errori, li logghiamo e restituiamo null
          LoggerService.error('Error fetching latest rental:', error);
          return new Observable<null>((observer) => {
            observer.next(null);
            observer.complete();
          });
        }),
      );
  }

  updateRentalMovement(rentalPracticeId: number, rentMovement: RentalMovementModel) {
    return this.http
      .post<RentalMovementModel>(
        this.baseUrl + `/rental-practices/${rentalPracticeId}/rentals/save/`,
        rentMovement,
      )
      .pipe(
        map((response: any) => {
          LoggerService.log(response, 'updateRentalMovement');

          return response;
        }),
      );
  }

  returnRentalMovement(practiceRentalMovementId: number, rentMovement: ReturnRentalMovementModel) {
    return this.http
      .post<ReturnRentalMovementModel>(
        this.baseUrl + `/rentals/${practiceRentalMovementId}/drop-off/`,
        rentMovement,
      )
      .pipe(
        map((response: any) => {
          LoggerService.log(response, 'returnRentalMovement');

          return response;
        }),
      );
  }

  downloadPrivacy(id: number) {
    return this.http.get(this.baseUrl + `/rentals/${id}/pdf-privacy?version=v2`, {
      headers: new HttpHeaders({ 'Content-Type': 'application/octet-stream' }),
      responseType: 'blob',
    });
  }
}
